import { config } from '../../config';
import { plainDescription } from '../lib/votings';
import { useNotifications } from '../providers/NotificationsProvider';
import { buildCleanUrl } from '../utils';
import React from 'react';
import { Helmet } from 'react-helmet';
import { useLocation } from 'react-router';

/**
 * Popis a obrázok, ktoré dostane stránka, čo si vlastné nedodá. Ten istý text
 * stojí aj staticky v `index.html` - ten číta každý robot, ktorý nespúšťa
 * JavaScript (Facebook, WhatsApp, Slack), takže sa nesmú rozísť.
 */
const DEFAULT_DESCRIPTION =
  'Podnety, hlasovania a novinky mesta Prešov na jednom mieste. Nahláste problém v meste, sledujte, ako ho mesto rieši, a zapojte sa do rozhodovania.';

/**
 * Náhľad pri zdieľaní, keď záznam vlastnú fotku nemá: erb na bielej podložke,
 * „Môj Prešov" a podnadpis na červenej mesta, 1200×630 - pomer, ktorý
 * Facebook, LinkedIn aj WhatsApp kreslia ako veľkú kartu. Písmo je Satoshi
 * a erb master z `@presov.sk/presov-ui-react`, text je prevedený na krivky
 * (opentype.js) a SVG vyrenderované do PNG cez `sharp`.
 *
 * PNG, nie SVG: SVG ako `og:image` tie siete neukážu - tak dopadlo
 * `logo-with-bg.svg`, ktoré tu bolo predtým.
 */
const DEFAULT_IMAGE = '/og-image.png';
const DEFAULT_IMAGE_WIDTH = 1200;
const DEFAULT_IMAGE_HEIGHT = 630;

/** Dĺžka, ktorú vyhľadávače v úryvku ešte ukážu celú. */
const DESCRIPTION_MAX_LENGTH = 200;

type Props = {
  /**
   * Názov stránky bez prípony. Bez neho je titul len „Môj Prešov". Detail
   * záznamu ho posiela s predponou druhu („Podnet: Diera v ceste na
   * Levočskej"), aby bolo v záložke aj v náhľade jasné, čo to je.
   */
  title?: string;
  /** Obyčajný text; HTML a markdown sa z neho odstránia. */
  description?: string | null;
  /** Absolútna alebo koreňová adresa obrázka (fotka podnetu, novinky). */
  image?: string | null;
  /**
   * Kanonická cesta záznamu, keď je iná než adresa v prehliadači - napr.
   * novinka otvorená v paneli nad mapou (`/?detail=<ID>`) má vlastnú stránku
   * `/novinky/<ID>`.
   */
  url?: string;
  /** `article` pre detail jedného záznamu, inak `website`. */
  type?: 'website' | 'article';
  /** Administrácia a profil do vyhľadávača nepatria. */
  noIndex?: boolean;
};

const absoluteUrl = (url: string) =>
  /^https?:\/\//.test(url) ? url : `${window.location.origin}${url}`;

/**
 * Meta údaje stránky - `<title>`, popis, Open Graph a Twitter karta.
 *
 * Kreslí ho [[RouteMetadata]] za každú routu z tabuľky v `App.tsx`, a detail,
 * ktorý o sebe vie viac (podnet, novinka, hlasovanie, projekt), ho vykreslí
 * ešte raz s vlastnými dátami. `react-helmet` zo zanorených inštancií berie
 * tú najhlbšiu, takže detail prebije routu bez toho, aby o nej musel vedieť.
 *
 * **Toto je pre vyhľadávače a pre záložku prehliadača, nie pre náhľad pri
 * zdieľaní.** Aplikácia je SPA, ktorú obsluhuje statický nginx, a roboty
 * sociálnych sietí JavaScript nespúšťajú - tie vidia len statické značky
 * v `index.html`. Náhľad konkrétneho podnetu na Facebooku by potreboval
 * server, ktorý značky doplní ešte pred odoslaním HTML.
 */
const PageMeta: React.FC<Props> = ({
  title,
  description,
  image,
  url: canonicalPath,
  type = 'website',
  noIndex = false,
}) => {
  const { unreadCount } = useNotifications();
  const { pathname, search } = useLocation();

  const fullTitle = title
    ? `${title} | ${config.PAGE_TITLE}`
    : config.PAGE_TITLE;
  const unread =
    unreadCount > 0 ? `(${unreadCount > 9 ? '9+' : unreadCount}) ` : '';
  const text =
    plainDescription(description).slice(0, DESCRIPTION_MAX_LENGTH) ||
    DEFAULT_DESCRIPTION;
  const url = canonicalPath
    ? absoluteUrl(canonicalPath)
    : buildCleanUrl(pathname, search);
  const imageUrl = absoluteUrl(image || DEFAULT_IMAGE);

  return (
    <Helmet>
      <title>{`${unread}${fullTitle}`}</title>
      <meta name='description' content={text} />
      {noIndex ? <meta name='robots' content='noindex, nofollow' /> : null}
      <link rel='canonical' href={url} />

      <meta property='og:type' content={type} />
      <meta property='og:site_name' content={config.PAGE_TITLE} />
      <meta property='og:locale' content='sk_SK' />
      <meta property='og:title' content={fullTitle} />
      <meta property='og:description' content={text} />
      <meta property='og:url' content={url} />
      <meta property='og:image' content={imageUrl} />
      {image ? null : (
        <meta property='og:image:width' content={`${DEFAULT_IMAGE_WIDTH}`} />
      )}
      {image ? null : (
        <meta property='og:image:height' content={`${DEFAULT_IMAGE_HEIGHT}`} />
      )}

      <meta name='twitter:card' content='summary_large_image' />
      <meta name='twitter:title' content={fullTitle} />
      <meta name='twitter:description' content={text} />
      <meta name='twitter:image' content={imageUrl} />
    </Helmet>
  );
};

export default PageMeta;
